import config from '@config';
import state from '@state';
import { markdownToHtml } from '@response/markdown';
import { log } from '@utils/logger';
import { isRtlLanguage } from '@utils/language';
import { AexResponse } from '@components/AexResponse';
import i18n from '@i18n';
import { deleteAexResponse, getAexResponse, saveAexResponse } from '@utils/sessionStorage';
import { AexResponseOptions } from 'types';

/**
 * Removes any existing response container content.
 */
export function removeResponse(): void {
	if (!state.currentInstance) {
		return;
	}

	document.querySelectorAll(`.aex-response-${state.currentInstance.index}`).forEach((response) => {
		const parent = response.parentNode;
		if (parent instanceof Element && parent.classList.contains('aex-response-wrapper')) {
			parent.remove();
		} else {
			response.remove();
		}
	});

	// Handle restored responses
	if (config.persistResults) {
		const restoredResponse = getAexResponse(state.currentInstance);

		if (restoredResponse) {
			document.querySelectorAll(`.aex-response-${restoredResponse.index}`).forEach((response) => {
				const parent = response.parentNode;
				if (parent instanceof Element && parent.classList.contains('aex-response-wrapper')) {
					parent.remove();
				} else {
					response.remove();
				}
			});

			if (restoredResponse.storageSelector) {
				deleteAexResponse(restoredResponse.storageSelector);
			}
		}
	}

}

/**
 * Shows a loading spinner in the response container.
 */
export async function showLoading(): Promise<void> {
	if (!state.currentInstance) {
		return;
	}

	removeResponse();

	const id = 'response-loading-' + state.currentInstance.index || '1';

	await AexResponse({
		target: state.currentInstance.responseContainer,
		position: state.currentInstance.responsePosition,
		type: 'loading',
		responseWrapperClass: state.currentInstance.responseWrapperClass,
		containerClass: state.currentInstance.responseClass,
		responseButtonClass: state.currentInstance.responseButtonClass,
		index: state.currentInstance.index || '1',
		id: id,
		content: {
			essentialInformation: config.loadingText || i18n.t('loadingText'),
		},
	});

	document.querySelector(`#${id} .aex-response__close-button`)?.addEventListener('click', () => {
		const currentResponse = document.querySelector(`#${id}`);
		const parent = currentResponse?.parentNode;
		if (parent instanceof Element && parent.classList.contains('aex-response-wrapper')) {
			parent.remove();
		} else {
			currentResponse?.remove();
		}

		state.abortManager.cancelCurrentRequest();
		(document.querySelector(`#aex-search-input-${state.currentInstance?.index || 1}`) as HTMLInputElement)?.focus();
	});
}

/**
 * Renders the search response or error to the UI.
 *
 * @param data - The response data to display.
 * @param inputType - The type of input that triggered the response (default is 'text').
 */
export async function displayResponse(data: Record<string, any>, inputType: string = 'text'): Promise<void> {
	log(
		i18n.t('log.displayMessage') +
		JSON.stringify(data, null, 2) +
		i18n.t('log.inputTypeMessage') +
		inputType,
	);

	removeResponse();

	const content = data.output || data;
	log(i18n.t('log.responseContentMessage', { prefix: i18n.t('log.prefix') }) + JSON.stringify(content, null, 2));

	if (!state.currentInstance) {
		return;
	}

	if (content.error || data.error) {
		const errorText = content.error || data.error;
		await AexResponse({
			target: state.currentInstance.responseContainer,
			position: state.currentInstance.responsePosition,
			type: 'error',
			responseWrapperClass: state.currentInstance.responseWrapperClass,
			containerClass: state.currentInstance.responseClass,
			responseButtonClass: state.currentInstance.responseButtonClass,
			index: state.currentInstance.index || '1',
			id: 'aex-error-message-' + state.currentInstance.index || '1',
			content: {
				errorText: errorText,
			},
			searchFallback: false,
			feedback: false,
		});
	} else {
		const textContent = extractText(content);
		const responseID = 'aex-response-' + state.currentInstance.index || '1';

		const responseArgs: AexResponseOptions = {
			target: state.currentInstance.responseContainer!, // assert non-null HTMLElement or string
			position: state.currentInstance.responsePosition as AexResponseOptions['position'],
			type: 'standard',
			responseWrapperClass: state.currentInstance.responseWrapperClass,
			containerClass: state.currentInstance.responseClass,
			responseButtonClass: state.currentInstance.responseButtonClass,
			index: (state.currentInstance.index ?? '1').toString(), // ensure string
			id: responseID,
			content: {
				query: content.originalQuery ?? content.query,
				formattedQuery: content.query,
				essentialInformation: textContent,
				primaryActions: content.primaryActions ?? [],
				timeSensitiveAlert: content.timeSensitiveAlert ?? '',
				shortRawQuery: content.shortRawQuery,
				keywordQuery: content.keywordQuery,
				sources: content.sources ?? [],
			},
			searchFallback: config.SearchFallback?.enabled ?? false,
			feedback: config.feedback?.enabled ?? false,
			rtl: isRtlLanguage(textContent),
			readAloud: state.currentInstance.enableReadAloud ?? false,
			lang: content.lang,
			storageSelector: state.currentInstance.searchForm || state.currentInstance.searchContainer,
		};

		await AexResponse(responseArgs);

		if (state.currentInstance) {
			saveAexResponse(responseArgs, state.currentInstance);
		}

		// Make sure the search input matches the query. Especially important for voice search
		const searchInput = document.querySelector<HTMLInputElement>(`${state.currentInstance?.searchForm} input[type="search"]`);

		if (searchInput && content?.originalQuery && searchInput.value !== content?.originalQuery) {
			searchInput.value = content.originalQuery ?? content.query;
			searchInput.dispatchEvent(new Event('input', { bubbles: true }));
			searchInput.dispatchEvent(new Event('change', { bubbles: true }));
		}

		handleSearchLinks();
	}
}

/**
 * Ensures search links in the same page still work when a # is present
 * */
export function handleSearchLinks() {
	if (config.SearchFallback?.enabled) {
		// Add event listener to handle searches that uses a # format which could stop the link working
		document.querySelectorAll('.aex-search-fallback a').forEach(link => {
			link.addEventListener('click', (e) => {
				// Narrow the type from Event to MouseEvent
				const mouseEvent = e as MouseEvent;

				// Allow modifier keys and non-left clicks to behave normally
				if (
					mouseEvent.ctrlKey || mouseEvent.metaKey || mouseEvent.shiftKey || mouseEvent.altKey || mouseEvent.button !== 0
				) {
					return;
				}

				mouseEvent.preventDefault();

				const href = (mouseEvent.currentTarget as HTMLAnchorElement).href;

				// Strip origin to compare paths/hashes only
				const current = window.location.href;
				const isSameHref = current === href;

				if (isSameHref) {
					// Same URL, force reload
					window.location.reload();
				} else {
					// Force hard navigation even for hash-based routes
					window.location.assign(href);
					(link as HTMLAnchorElement).click();
				}
			});
		});

	}
}

/**
 * Extracts the best available text from the response object.
 *
 * @param content - The response content object.
 * @returns The extracted text as a string.
 */
function extractText(content: any): string {
	let textContent = i18n.t('error.noResponseContent');;

	if (typeof content === 'string') {
		// Direct string response
		textContent = content;
	} else if (typeof content === 'object') {
		// Object response - check multiple possible content fields
		textContent =
			content.essentialInformation ||
			content.content ||
			content.response ||
			content.message ||
			content.text;

		// Check nested output object if no content found
		if (!textContent && content.output) {
			textContent =
				content.output.essentialInformation ||
				content.output.content ||
				content.output.response ||
				content.output.message ||
				content.output.text;
		}
	}

	return markdownToHtml(textContent);
}
